'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import MuteToggle from '@/components/MuteToggle';

interface LeaderboardPlayer {
  id: string;
  full_name: string;
  arena_wins: number;
  best_streak: number;
  lifetime_kills: number;
  is_premium: boolean;
}

type SortKey = 'arena_wins' | 'best_streak' | 'lifetime_kills';

const SORT_OPTIONS: { key: SortKey; label: string; icon: string; color: string; rgb: string }[] = [
  { key: 'arena_wins', label: 'Arena Wins', icon: '⚔️', color: '#ff3b6b', rgb: '255,59,107' },
  { key: 'best_streak', label: 'Best Streak', icon: '🔥', color: '#ffd23f', rgb: '255,210,63' },
  { key: 'lifetime_kills', label: 'Lifetime Kills', icon: '💀', color: '#7b5bff', rgb: '123,91,255' },
];

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="text-2xl">🥇</span>;
  if (rank === 2) return <span className="text-2xl">🥈</span>;
  if (rank === 3) return <span className="text-2xl">🥉</span>;
  return (
    <span
      className="font-black text-sm w-8 h-8 flex items-center justify-center rounded-full"
      style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--muted-foreground)' }}
    >
      {rank}
    </span>
  );
}

function PlayerInitials({ name, isPremium }: { name: string; isPremium: boolean }) {
  const initials = (name || 'PL').slice(0, 2).toUpperCase();
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center font-black text-sm flex-shrink-0 relative"
      style={{
        background: isPremium
          ? 'linear-gradient(135deg, #ffd23f, #ff9500)'
          : 'linear-gradient(135deg, #7b5bff, #ff3b6b)',
        color: '#fff',
      }}
    >
      {initials}
      {isPremium && (
        <span
          className="absolute -top-1 -right-1 text-xs leading-none"
          title="Premium"
        >
          👑
        </span>
      )}
    </div>
  );
}

export default function LeaderboardClient() {
  const router = useRouter();
  const supabase = createClient();

  const [players, setPlayers] = useState<LeaderboardPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('arena_wins');
  const [liveIndicator, setLiveIndicator] = useState(false);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('id, full_name, arena_wins, best_streak, lifetime_kills, is_premium')
        .order(sortKey, { ascending: false })
        .limit(100);

      if (fetchError) throw fetchError;
      setPlayers(data || []);
      setError('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [sortKey]);

  useEffect(() => {
    setLoading(true);
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Live real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('leaderboard_live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_profiles' },
        () => {
          // Flash live indicator
          setLiveIndicator(true);
          setTimeout(() => setLiveIndicator(false), 1200);
          fetchLeaderboard();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchLeaderboard]);

  const sorted = [...players].sort((a, b) => b[sortKey] - a[sortKey]);

  const activeSortOption = SORT_OPTIONS.find((o) => o.key === sortKey)!;

  return (
    <div className="relative z-10 flex flex-col min-h-screen">
      {/* Header */}
      <header className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <AppLogo size={28} onClick={() => router.push('/')} />
          <span
            className="font-bold text-base tracking-tight text-foreground cursor-pointer"
            onClick={() => router.push('/')}
          >
            Unbored
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MuteToggle />
          <button
            onClick={() => router.push('/game-stage')}
            className="btn-primary px-4 py-2 text-sm font-bold"
          >
            Play ⚡
          </button>
        </div>
      </header>

      <div className="flex-1 px-5 py-4 max-w-2xl mx-auto w-full flex flex-col gap-6 pb-12">
        {/* Title */}
        <div className="text-center fade-in">
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-3"
            style={{
              background: 'rgba(255,59,107,0.12)',
              border: '1px solid rgba(255,59,107,0.3)',
              color: '#ff3b6b',
            }}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{
                background: liveIndicator ? '#36e2a4' : '#ff3b6b',
                boxShadow: liveIndicator ? '0 0 8px #36e2a4' : 'none',
                transition: 'all 0.3s ease',
              }}
            />
            {liveIndicator ? 'Updating…' : 'Live'}
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground">
            Global Leaderboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1 font-medium">
            All-time rankings · synced live
          </p>
        </div>

        {/* Sort tabs */}
        <div
          className="flex gap-2 p-1 rounded-2xl"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSortKey(opt.key)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-black transition-all"
              style={{
                background: sortKey === opt.key ? `rgba(${opt.rgb}, 0.15)` : 'transparent',
                color: sortKey === opt.key ? opt.color : 'var(--muted-foreground)',
                border: sortKey === opt.key ? `1px solid rgba(${opt.rgb}, 0.35)` : '1px solid transparent',
              }}
            >
              <span>{opt.icon}</span>
              <span className="hidden sm:inline">{opt.label}</span>
            </button>
          ))}
        </div>

        {/* Top 3 podium */}
        {!loading && sorted.length >= 3 && (
          <div className="flex items-end justify-center gap-3 px-2">
            {/* 2nd */}
            <div className="flex flex-col items-center gap-2 flex-1">
              <PlayerInitials name={sorted[1]?.full_name} isPremium={sorted[1]?.is_premium} />
              <div className="text-center">
                <p className="text-xs font-bold text-foreground truncate max-w-[80px]">
                  {sorted[1]?.full_name || 'Player'}
                </p>
                <p className="text-lg font-black" style={{ color: activeSortOption.color }}>
                  {sorted[1]?.[sortKey] ?? 0}
                </p>
              </div>
              <div
                className="w-full rounded-t-xl flex items-center justify-center py-3"
                style={{
                  background: 'rgba(192,192,192,0.12)',
                  border: '1px solid rgba(192,192,192,0.2)',
                  minHeight: '56px',
                }}
              >
                <span className="text-2xl">🥈</span>
              </div>
            </div>

            {/* 1st */}
            <div className="flex flex-col items-center gap-2 flex-1">
              <div
                className="w-3 h-3 rounded-full animate-pulse"
                style={{ background: '#ffd23f', boxShadow: '0 0 12px #ffd23f' }}
              />
              <PlayerInitials name={sorted[0]?.full_name} isPremium={sorted[0]?.is_premium} />
              <div className="text-center">
                <p className="text-xs font-bold text-foreground truncate max-w-[80px]">
                  {sorted[0]?.full_name || 'Player'}
                </p>
                <p className="text-xl font-black" style={{ color: activeSortOption.color }}>
                  {sorted[0]?.[sortKey] ?? 0}
                </p>
              </div>
              <div
                className="w-full rounded-t-xl flex items-center justify-center py-5"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,210,63,0.15), rgba(255,210,63,0.05))',
                  border: '1px solid rgba(255,210,63,0.3)',
                  minHeight: '80px',
                }}
              >
                <span className="text-3xl">🥇</span>
              </div>
            </div>

            {/* 3rd */}
            <div className="flex flex-col items-center gap-2 flex-1">
              <PlayerInitials name={sorted[2]?.full_name} isPremium={sorted[2]?.is_premium} />
              <div className="text-center">
                <p className="text-xs font-bold text-foreground truncate max-w-[80px]">
                  {sorted[2]?.full_name || 'Player'}
                </p>
                <p className="text-lg font-black" style={{ color: activeSortOption.color }}>
                  {sorted[2]?.[sortKey] ?? 0}
                </p>
              </div>
              <div
                className="w-full rounded-t-xl flex items-center justify-center py-2"
                style={{
                  background: 'rgba(205,127,50,0.1)',
                  border: '1px solid rgba(205,127,50,0.2)',
                  minHeight: '44px',
                }}
              >
                <span className="text-2xl">🥉</span>
              </div>
            </div>
          </div>
        )}

        {/* Full rankings list */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {/* List header */}
          <div
            className="flex items-center gap-3 px-4 py-3 text-xs font-black uppercase tracking-widest"
            style={{
              background: 'rgba(255,255,255,0.04)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              color: 'var(--muted-foreground)',
            }}
          >
            <span className="w-8 text-center">#</span>
            <span className="flex-1">Player</span>
            <span className="flex items-center gap-1" style={{ color: '#ff3b6b', minWidth: '56px', justifyContent: 'flex-end' }}>
              ⚔️ <span className="hidden sm:inline">Wins</span>
            </span>
            <span className="flex items-center gap-1" style={{ color: '#ffd23f', minWidth: '56px', justifyContent: 'flex-end' }}>
              🔥 <span className="hidden sm:inline">Streak</span>
            </span>
            <span className="flex items-center gap-1" style={{ color: '#7b5bff', minWidth: '56px', justifyContent: 'flex-end' }}>
              💀 <span className="hidden sm:inline">Kills</span>
            </span>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col gap-0">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={`skel-${i}`}
                  className="flex items-center gap-3 px-4 py-3 animate-pulse"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                >
                  <div className="w-8 h-8 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  <div className="w-10 h-10 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  <div className="flex-1 h-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  <div className="w-10 h-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  <div className="w-10 h-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  <div className="w-10 h-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} />
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex flex-col items-center gap-3 py-12 px-6 text-center">
              <span className="text-4xl">⚠️</span>
              <p className="text-sm text-muted-foreground">{error}</p>
              <button
                onClick={fetchLeaderboard}
                className="btn-primary px-5 py-2 text-sm font-bold"
              >
                Retry
              </button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && sorted.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-12 px-6 text-center">
              <span className="text-4xl">🏆</span>
              <p className="font-bold text-foreground">No players yet</p>
              <p className="text-sm text-muted-foreground">Be the first to play and claim the top spot!</p>
              <button
                onClick={() => router.push('/game-stage')}
                className="btn-primary px-5 py-2 text-sm font-bold"
              >
                Play now ⚡
              </button>
            </div>
          )}

          {/* Rows */}
          {!loading && !error && sorted.map((player, idx) => {
            const rank = idx + 1;
            const isTop3 = rank <= 3;
            return (
              <div
                key={player.id}
                className="flex items-center gap-3 px-4 py-3 transition-colors"
                style={{
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: isTop3
                    ? `rgba(${activeSortOption.rgb}, 0.04)`
                    : 'transparent',
                }}
              >
                {/* Rank */}
                <div className="w-8 flex items-center justify-center flex-shrink-0">
                  <RankBadge rank={rank} />
                </div>

                {/* Avatar */}
                <PlayerInitials name={player.full_name} isPremium={player.is_premium} />

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-foreground truncate">
                    {player.full_name || 'Anonymous'}
                  </p>
                  {player.is_premium && (
                    <p className="text-xs font-semibold" style={{ color: '#ffd23f' }}>
                      Premium
                    </p>
                  )}
                </div>

                {/* Arena Wins */}
                <div
                  className="text-right font-black text-sm"
                  style={{
                    color: sortKey === 'arena_wins' ? '#ff3b6b' : 'var(--muted-foreground)',
                    minWidth: '56px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {player.arena_wins ?? 0}
                </div>

                {/* Best Streak */}
                <div
                  className="text-right font-black text-sm"
                  style={{
                    color: sortKey === 'best_streak' ? '#ffd23f' : 'var(--muted-foreground)',
                    minWidth: '56px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {player.best_streak ?? 0}
                </div>

                {/* Lifetime Kills */}
                <div
                  className="text-right font-black text-sm"
                  style={{
                    color: sortKey === 'lifetime_kills' ? '#7b5bff' : 'var(--muted-foreground)',
                    minWidth: '56px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {player.lifetime_kills ?? 0}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        {!loading && sorted.length > 0 && (
          <p className="text-center text-xs text-muted-foreground">
            Showing top {sorted.length} players · Stats sync live
          </p>
        )}
      </div>
    </div>
  );
}
